import React from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import useMaturityData from "../hooks/useMaturityData";
import { LEVEL_COLORS, LEVELS } from "../utils/levelColors";
import "./dashboard.css";

const SHORT_LABELS = {
  "Data Collection Process": "Collection",
  "Data Management - Governance": "Governance",
  "Data Management - Infrastructure": "Infrastructure",
  "Data Provenance": "Provenance",
  "Data Access": "Access",
  "Data Analytics Environment": "Analytics",
  "Data Enhancement - Augmentation": "Augmentation",
  "Data Enhancement - Enrichment": "Enrichment",
  "Data Model": "Data Model",
  "Data Dictionary": "Dictionary",
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="radar-tooltip">
      <div className="radar-tooltip-title">{d.fullName}</div>
      <div className="radar-tooltip-row">
        <span>Level</span>
        <span
          className="radar-tooltip-badge"
          style={{ backgroundColor: LEVEL_COLORS[d.level]?.bg ?? "#555" }}
        >
          {d.level}
        </span>
      </div>
      <div className="radar-tooltip-row">
        <span>Score</span>
        <span>{d.score} / {d.maxScore} pts</span>
      </div>
      <div className="radar-tooltip-row">
        <span>Completion</span>
        <span>{d.pct}%</span>
      </div>
      {d.nextLevel && (
        <div className="radar-tooltip-row">
          <span>To Level {d.nextLevel}</span>
          <span>+{d.pointsToNextLevel} pts</span>
        </div>
      )}
    </div>
  );
};

const MaturityRadar = () => {
  const { levelProgress, loading, error } = useMaturityData();

  if (loading) return <div className="dashboard-loading">Loading radar…</div>;
  if (error) return <div className="dashboard-loading">{error}</div>;

  if (!levelProgress) {
    return (
      <>
        <div className="dashboard-header">
          <h1 className="dashboard-title">Maturity Radar</h1>
        </div>
        <div className="dashboard-next-steps">
          <p className="dashboard-next-steps-text">
            Complete the questionnaire to see your maturity radar.
          </p>
        </div>
      </>
    );
  }

  const radarData = Object.entries(levelProgress).map(([dim, data]) => ({
    subject: SHORT_LABELS[dim] || dim,
    fullName: dim,
    level: data.achievedLevel,
    score: data.score,
    maxScore: data.maxScore,
    pct: data.maxScore > 0 ? Math.round((data.score / data.maxScore) * 100) : 0,
    nextLevel: data.nextLevel,
    pointsToNextLevel: data.pointsToNextLevel,
  }));

  return (
    <>
      <div className="dashboard-header">
        <h1 className="dashboard-title">Maturity Radar</h1>
        <p className="dashboard-subtitle">
          Visual overview of your maturity profile across all dimensions
        </p>
      </div>

      <div className="radar-card">
        <h2 className="dashboard-section-title">Radar Overview</h2>
        <p className="dashboard-table-note">
          Each axis represents one dimension. The shaded area shows your achieved level (scale 1–5).
        </p>
        <div className="radar-chart-wrapper">
          <ResponsiveContainer width="100%" height={440}>
            <RadarChart data={radarData} margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
              <PolarGrid stroke="#dde3ee" />
              <PolarAngleAxis
                dataKey="subject"
                tick={{ fill: "#1e3054", fontSize: 12, fontWeight: 600 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 5]}
                tickCount={6}
                tick={{ fill: "#aab", fontSize: 10 }}
                stroke="#dde3ee"
              />
              <Radar
                name="Maturity Level"
                dataKey="level"
                stroke="#1e3054"
                fill="#1e3054"
                fillOpacity={0.18}
                strokeWidth={2.5}
                dot={{ r: 5, fill: "#1e3054", stroke: "#fff", strokeWidth: 2 }}
              />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="radar-legend">
          {LEVELS.map((l) => (
            <div key={l} className="radar-legend-item">
              <span
                className="radar-legend-dot"
                style={{ backgroundColor: LEVEL_COLORS[l].bg }}
              />
              Level {l}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default MaturityRadar;
